import React, { useCallback, useEffect, useState } from 'react'
import {
  Button, DatePicker, Drawer, Form, Input, Modal, Space, Table, Tag, Typography, message,
} from 'antd'
import type { ColumnsType } from 'antd/es/table'
import dayjs, { Dayjs } from 'dayjs'
import { get, patch, post } from '../../lib/request'
import type { PageData } from '../../lib/api'
import { useAuthStore } from '../../stores/auth'
import { hasPermission } from '../../utils/permissions'

const { Text } = Typography

// 成交记录：复用 /admin/leads?status=dealt。
// 注意：行 id 是 leadId，不是 dealId —— 收款相关接口一律用 r.deal.id。
interface DealRow {
  id: number // leadId
  name: string
  phone: string
  company?: string
  createdAt: string
  deal?: {
    id: number // dealId（收款接口的路径参数）
    paymentCollectedAt?: string | null
    firstMonthRent?: string | number | null
    signedAt?: string | null
  } | null
}

interface PaymentLog {
  field: string
  oldValue?: string | null
  newValue?: string | null
  reason?: string | null
  changedBy?: number | null
  changedAt?: string
}

const fmtDate = (t?: string | null) => (t ? dayjs(t).format('YYYY-MM-DD') : '—')
const fmtTime = (t?: string | null) => (t ? dayjs(t).format('YYYY-MM-DD HH:mm') : '—')

const PaymentsPage: React.FC = () => {
  const role = useAuthStore((s) => s.user?.role)
  const canRecord = hasPermission(role, 'REWARD_OPERATE')
  // 「修改收款日期」按钮仅 finance_manager 渲染（冻结规则）
  const canModify = hasPermission(role, 'PAYMENT_MODIFY')

  const [list, setList] = useState<DealRow[]>([])
  const [total, setTotal] = useState(0)
  const [loading, setLoading] = useState(false)
  const [page, setPage] = useState(1)
  const [pageSize, setPageSize] = useState(10)

  const [recordTarget, setRecordTarget] = useState<DealRow | null>(null)
  const [modifyTarget, setModifyTarget] = useState<DealRow | null>(null)
  const [recordForm] = Form.useForm<{ paymentCollectedAt: Dayjs }>()
  const [modifyForm] = Form.useForm<{ paymentCollectedAt: Dayjs; reason: string }>()
  const [submitLoading, setSubmitLoading] = useState(false)

  const [logsDeal, setLogsDeal] = useState<DealRow | null>(null)
  const [logs, setLogs] = useState<PaymentLog[]>([])
  const [logsLoading, setLogsLoading] = useState(false)

  const fetchList = useCallback(() => {
    setLoading(true)
    // 后端 /admin/leads 返回信封为 { total, page, pageSize, items }，字段名是 items 不是 list
    get<PageData<DealRow>>('/admin/leads', {
      params: { page, pageSize, status: 'dealt' },
    })
      .then((data) => {
        setList(data.items ?? [])
        setTotal(data.total ?? 0)
      })
      .catch((err: unknown) => {
        const msg =
          (err as { response?: { data?: { message?: string } } })?.response?.data?.message ??
          '成交列表加载失败（后端可能未启动）'
        message.warning(msg)
        setList([])
      })
      .finally(() => setLoading(false))
  }, [page, pageSize])

  useEffect(() => {
    fetchList()
  }, [fetchList])

  const openLogs = (record: DealRow) => {
    if (!record.deal?.id) {
      message.warning('该线索暂无成交单，无收款日志')
      return
    }
    setLogsDeal(record)
    setLogs([])
    setLogsLoading(true)
    get<PaymentLog[]>(`/admin/deals/${record.deal.id}/payment-logs`)
      .then((data) => setLogs(Array.isArray(data) ? data : []))
      .catch(() => message.warning('修改日志加载失败（后端可能未启动）'))
      .finally(() => setLogsLoading(false))
  }

  const handleRecord = async () => {
    if (!recordTarget?.deal?.id) return
    const values = await recordForm.validateFields()
    setSubmitLoading(true)
    try {
      const data = await post<{ rewardCalc?: { created: number } | null; rewardCalcError?: string | null }>(
        `/admin/deals/${recordTarget.deal.id}/deposit`,
        { paymentCollectedAt: values.paymentCollectedAt.format('YYYY-MM-DD') }
      )
      if (data?.rewardCalcError) {
        message.warning(`收款日期已录入，但自动核算失败：${data.rewardCalcError}（可在答谢管理手动点「一键核算」）`)
      } else if (data?.rewardCalc) {
        message.success(`收款日期录入成功，已自动核算生成 ${data.rewardCalc.created} 条待审批奖励`)
      } else {
        message.success('收款日期录入成功')
      }
      setRecordTarget(null)
      recordForm.resetFields()
      fetchList()
    } catch (err: unknown) {
      const msg =
        (err as { response?: { data?: { message?: string } } })?.response?.data?.message ??
        '录入失败'
      message.error(msg)
    } finally {
      setSubmitLoading(false)
    }
  }

  const handleModify = async () => {
    if (!modifyTarget?.deal?.id) return
    const values = await modifyForm.validateFields()
    setSubmitLoading(true)
    try {
      await patch(`/admin/deals/${modifyTarget.deal.id}/deposit`, {
        paymentCollectedAt: values.paymentCollectedAt.format('YYYY-MM-DD'),
        reason: values.reason,
      })
      message.success('收款日期已修改并留痕')
      setModifyTarget(null)
      modifyForm.resetFields()
      fetchList()
    } catch (err: unknown) {
      const msg =
        (err as { response?: { data?: { message?: string } } })?.response?.data?.message ??
        '修改失败（仅财务经理可操作）'
      message.error(msg)
    } finally {
      setSubmitLoading(false)
    }
  }

  const columns: ColumnsType<DealRow> = [
    {
      title: '成交单',
      key: 'dealId',
      render: (_: unknown, r) => (r.deal?.id ? `#D-${r.deal.id}` : '—'),
    },
    { title: '潜客', dataIndex: 'name', key: 'name', render: (v: string) => v ?? '—' },
    { title: '手机号', dataIndex: 'phone', key: 'phone' },
    { title: '公司', dataIndex: 'company', key: 'company', render: (v?: string) => v ?? '—' },
    { title: '成交时间', dataIndex: 'createdAt', key: 'createdAt', render: fmtDate },
    {
      title: '两押一租到账日期',
      key: 'paymentCollectedAt',
      render: (_: unknown, r) =>
        r.deal?.paymentCollectedAt ? (
          <Tag color="success">{fmtDate(r.deal.paymentCollectedAt)}</Tag>
        ) : (
          <Tag color="error">未录入</Tag>
        ),
    },
    {
      title: '操作',
      key: 'actions',
      fixed: 'right',
      render: (_: unknown, r) => (
        <Space size={4} wrap>
          {canRecord && !!r.deal?.id && !r.deal.paymentCollectedAt && (
            <Button
              size="small"
              type="primary"
              style={{ minHeight: 32 }}
              onClick={() => {
                setRecordTarget(r)
                recordForm.resetFields()
              }}
            >
              录入收款日期
            </Button>
          )}
          {canModify && !!r.deal?.paymentCollectedAt && (
            <Button
              size="small"
              style={{ minHeight: 32, borderColor: '#F39C12', color: '#F39C12' }}
              onClick={() => {
                setModifyTarget(r)
                modifyForm.setFieldsValue({
                  paymentCollectedAt: dayjs(r.deal?.paymentCollectedAt),
                  reason: '',
                })
              }}
            >
              修改收款日期
            </Button>
          )}
          <Button size="small" style={{ minHeight: 32 }} onClick={() => openLogs(r)}>
            修改日志
          </Button>
        </Space>
      ),
    },
  ]

  return (
    <>
      <Table<DealRow>
        rowKey="id"
        loading={loading}
        columns={columns}
        dataSource={list}
        scroll={{ x: 'max-content' }}
        style={{ background: '#fff', borderRadius: 12 }}
        pagination={{
          current: page,
          pageSize,
          total,
          showSizeChanger: true,
          showTotal: (t) => `共 ${t} 条`,
          onChange: (p, ps) => {
            setPage(p)
            setPageSize(ps)
          },
        }}
      />

      <Modal
        title="录入收款日期（两押一租到账）"
        open={!!recordTarget}
        onOk={handleRecord}
        onCancel={() => setRecordTarget(null)}
        okText="确认录入"
        cancelText="取消"
        confirmLoading={submitLoading}
      >
        <Form form={recordForm} layout="vertical">
          <Form.Item
            name="paymentCollectedAt"
            label="到账日期"
            rules={[{ required: true, message: '请选择到账日期' }]}
          >
            <DatePicker style={{ width: '100%' }} />
          </Form.Item>
        </Form>
        <Text type="secondary" style={{ fontSize: 12 }}>
          录入后触发奖励发放条件；录入后仅财务经理可修改，修改须填写原因并自动留痕。
        </Text>
      </Modal>

      <Modal
        title="修改收款日期（仅财务经理）"
        open={!!modifyTarget}
        onOk={handleModify}
        onCancel={() => setModifyTarget(null)}
        okText="确认修改"
        cancelText="取消"
        confirmLoading={submitLoading}
        okButtonProps={{ danger: true }}
      >
        <div
          style={{
            background: '#FEF5E7',
            borderRadius: 8,
            padding: '8px 12px',
            color: '#F39C12',
            marginBottom: 12,
            fontSize: 13,
          }}
        >
          修改前后：{fmtDate(modifyTarget?.deal?.paymentCollectedAt)} → （表单新值）
        </div>
        <Form form={modifyForm} layout="vertical">
          <Form.Item
            name="paymentCollectedAt"
            label="新到账日期"
            rules={[{ required: true, message: '请选择新到账日期' }]}
          >
            <DatePicker style={{ width: '100%' }} />
          </Form.Item>
          <Form.Item
            name="reason"
            label="修改原因（必填）"
            rules={[{ required: true, message: '修改原因必填' }]}
          >
            <Input.TextArea rows={3} placeholder="例：银行到账回单日期以 16 日为准" />
          </Form.Item>
        </Form>
        <Text type="secondary" style={{ fontSize: 12 }}>
          留痕内容：修改人（财务经理）、前后值、原因、时间，写入收款日期修改日志，不可删除。
        </Text>
      </Modal>

      <Drawer
        title={`收款日期修改日志 · 成交单 #D-${logsDeal?.deal?.id ?? ''}`}
        open={!!logsDeal}
        onClose={() => setLogsDeal(null)}
        width={Math.min(520, window.innerWidth)}
        loading={logsLoading}
      >
        {logs.length > 0 ? (
          logs.map((log, i) => (
            <div key={i} style={{ display: 'flex', gap: 10, padding: '8px 0', fontSize: 13 }}>
              <span
                style={{
                  width: 8, height: 8, borderRadius: '50%', flexShrink: 0, marginTop: 5,
                  background: log.oldValue ? '#F39C12' : '#27AE60',
                }}
              />
              <span style={{ width: 130, flexShrink: 0, color: '#7F8C8D' }}>{fmtTime(log.changedAt)}</span>
              <span>
                {log.oldValue
                  ? `修改：${log.oldValue} → ${log.newValue ?? '—'} ｜ 原因：${log.reason ?? '—'}（操作人 #${log.changedBy ?? '—'}）`
                  : `录入：${log.newValue ?? '—'}（操作人 #${log.changedBy ?? '—'}）`}
              </span>
            </div>
          ))
        ) : (
          <Text type="secondary">暂无修改日志</Text>
        )}
      </Drawer>
    </>
  )
}

export default PaymentsPage
